import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarSpace,
} from '@backstage/core-components';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import type { NavContentComponentProps } from '@backstage/plugin-app-react';
import { SidebarLogo } from './SidebarLogo';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import HomeIcon from '@material-ui/icons/Home';
import LayersIcon from '@material-ui/icons/Layers';
import ChatIcon from '@material-ui/icons/Chat';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import { devToolsAdministerPermission } from '@backstage/plugin-devtools-common';
import { usePermission } from '@backstage/plugin-permission-react';
import { useIsPlatformAdmin } from './useIsPlatformAdmin';
import { ProvisionMenu } from './ProvisionMenu';
import { ServiceDrawer } from './ServiceDrawer';
import { PolicyAdministrationNavItem } from './PolicyAdministrationNavItem';
import { ServiceAccountsNavItem } from './ServiceAccountsNavItem';
import { useAdministrationTenants } from './useAdministrationTenants';

/**
 * Declared as a named component rather than inline in the blueprint params so
 * that it is one for the linter too - the nav content calls hooks, and the
 * rules-of-hooks check goes by the name.
 */
const AevenNav = ({ navItems }: NavContentComponentProps) => {
  const nav = navItems.withComponent(item => (
    <SidebarItem icon={() => item.icon} to={item.href} text={item.title} />
  ));
  const isPlatformAdmin = useIsPlatformAdmin();
  // One lookup for both admin entries: it fans out a request per tenant.
  const administeredTenants = useAdministrationTenants();
  // Its own permission rather than isPlatformAdmin: a tenant admin granted
  // read-only DevTools should find the page too.
  const { allowed: canOpenDevTools } = usePermission({
    permission: devToolsAdministerPermission,
  });

  // Consumed but not rendered here.
  nav.take('page:search'); // Using the search modal instead.
  nav.take('page:scaffolder'); // Re-labelled "Provision" below.
  nav.take('page:assistant'); // Pinned in the menu below.

  const catalogItem = nav.take('page:catalog');
  const visualizerItem = nav.take('page:app-visualizer');
  const devToolsItem = nav.take('page:devtools');
  const settingsItem = nav.take('page:user-settings');

  // Everything a plugin contributes beyond the items above is deliberately
  // kept out of the sidebar: API docs, the catalog graph, TechDocs and the
  // Kubernetes page are debugging surfaces rather than tenant workflows,
  // and the notifications entry among them duplicated the pinned one at
  // the bottom. The routes stay mounted, so deep links keep working - this
  // only narrows what the navigation advertises.
  nav.rest();

  return (
    <Sidebar>
      <SidebarLogo />
      <ServiceDrawer />
      <SidebarDivider />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        <SidebarItem icon={HomeIcon} to="/" text="Home" />
        <SidebarItem icon={LayersIcon} to="/my-stacks" text="My stacks" />
        <ProvisionMenu />
        <SidebarItem icon={ChatIcon} to="/assistant" text="Assistant" />
        <PolicyAdministrationNavItem tenants={administeredTenants} />
        <ServiceAccountsNavItem tenants={administeredTenants} />
        {isPlatformAdmin && (
          <>
            <SidebarDivider />
            {catalogItem}
          </>
        )}
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <NotificationsSidebarItem />
      <SidebarDivider />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        {isPlatformAdmin && visualizerItem}
        {canOpenDevTools && devToolsItem}
        {settingsItem}
      </SidebarGroup>
    </Sidebar>
  );
};

export const SidebarContent = NavContentBlueprint.make({
  params: { component: AevenNav },
});
